import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Grid,
  FormControlLabel,
} from "@mui/material";
import { CustomAlert } from "../../Common/Components/CustomAlert";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: 600,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

export default function EditShareholder({
  open,
  handleClose,
  onSave,
  company,
  initialData = {},
}) {
  const { t, i18n } = useTranslation(["Company"]);
  const userState = useSelector((state) => state.user);

  const defaultForm = {
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    noCellulaire: "",
    langue: "FR",
    department: "",
    pourcentageAction: 0,
    storeNumber: company?.noAccpac || "",
    companyName: company?.nom || "",
    noCompany: company?.noCompagnie || "",
    openingDate: company?.dateOuverture || "",
    isActive: true,
  };

  const [formData, setFormData] = useState(defaultForm);

  const [countriesData, setCountriesData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");

  const [provincesData, setProvincesData] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [province, setProvince] = useState("");
  // --- ALERT STATE ---
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState("");

  // Génération automatique du username
  const generateUsername = (firstName, lastName) => {
    if (!firstName || !lastName) return "";
    return (firstName[0] + lastName).toLowerCase().replace(/\s+/g, "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "firstName" || name === "lastName") {
        const f = name === "firstName" ? value : updated.firstName;
        const l = name === "lastName" ? value : updated.lastName;
        updated.username = generateUsername(f, l);
      }
      return updated;
    });
  };

  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    setFormData((prev) => ({ ...prev, isActive: checked }));
  };

  const saveShareholder = async () => {
    const dto = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      username: formData.username,
      email: formData.email,
      noCellulaire: formData.noCellulaire,
      langue: formData.langue,
      exploitant: formData.isActive, // correspond à Exploitant dans DTO
      country,
      province,
      region,
      adresse: formData.adresse || "", // si tu ajoutes un champ Adresse plus tard
      ville: formData.ville || "",
      codePostal: formData.codePostal || "",
      pourcentageAction: parseInt(formData.pourcentageAction) || 0,
      noCompany: company?.noCompagnie || null,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/company/AssocierShareholder`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(dto),
        },
      );

      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Erreur API: ${error}`);
      }

      const data = await res.json();
      console.log("Shareholder saved:", data);
      return data;
    } catch (error) {
      console.error("Erreur lors de l’enregistrement du shareholder:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      // Vérifier si le user existe
      const { exists, message, emailExists, usernameExists } =
        await checkUserExists(formData.email, formData.username);

      if (exists) {
        let msg = message;
        if (emailExists && !usernameExists) msg = "Cet email est déjà utilisé.";
        if (!emailExists && usernameExists)
          msg = "Ce nom d'utilisateur est déjà pris.";
        if (emailExists && usernameExists)
          msg = "L'email et le nom d'utilisateur sont déjà utilisés.";

        setAlertType("error");
        setAlertMessage(msg);
        setAlertOpen(true);
        return;
      }

      const saved = await saveShareholder();
      onSave(saved);
      handleClose();
    } catch (error) {
      alert("Erreur lors de la sauvegarde du shareholder.");
    }
  };

  // --- vérifier si le user existe selon un critère (souvent l’email ou le username). ---

  const checkUserExists = async (email, username) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Company/CheckUserExists?email=${encodeURIComponent(email)}&username=${encodeURIComponent(username)}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      const text = await res.text(); // on lit le texte brut
      let data;
      try {
        data = JSON.parse(text); // on essaie de parser le JSON
      } catch {
        // si ce n'est pas du JSON
        data = { exists: false, message: "Erreur API: " + text };
      }

      return data; // { exists, emailExists, usernameExists, message }
    } catch (error) {
      return { exists: false, message: "Erreur de connexion avec le serveur." };
    }
  };

  // --- Fetch Countries ---
  const fetchCountries = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Localization/GetCountries`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );
      const data = await res.json();
      setCountriesData(data);
      setCountries(
        data.map((c) => (i18n.language === "fr" ? c.nomFr : c.nomEn)),
      );
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchProvinces = async () => {
    if (!country) return;
    const selected = countriesData.find(
      (c) => (i18n.language === "fr" ? c.nomFr : c.nomEn) === country,
    );
    if (!selected?.noPays) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Localization/GetProvinceByCountry/${selected.noPays}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );
      const data = await res.json();
      setProvincesData(data);
      setProvinces(
        data.map((p) => (i18n.language === "fr" ? p.nomFr : p.nomEn)),
      );
    } catch (error) {
      console.error("Error fetching provinces:", error);
      setProvinces([]);
      setProvincesData([]);
    }
  };

  const fetchRegions = async () => {
    if (!province) return;
    const selected = provincesData.find(
      (p) => (i18n.language === "fr" ? p.nomFr : p.nomEn) === province,
    );
    if (!selected?.noProvince) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Localization/GetRegionsByProvinces/${selected.noProvince}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );
      const data = await res.json();
      setRegions(data.map((r) => (i18n.language === "fr" ? r.nomFr : r.nomEn)));
    } catch (error) {
      console.error("Error fetching regions:", error);
      setRegions([]);
    }
  };

  useEffect(() => {
    if (open) {
      // Reset form à l'ouverture
      setFormData({ ...defaultForm, ...initialData });
      setCountry(initialData.country || "");
      setProvince(initialData.province || "");
      setRegion(initialData.region || "");
      fetchCountries();
    }
  }, [open]);

  useEffect(() => {
    fetchProvinces();
  }, [country]);

  useEffect(() => {
    fetchRegions();
  }, [province]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <h2 style={{ marginBottom: 16 }}>
          {initialData?.id ? "Edit Shareholder" : "Add New Shareholder"}{" "}
          {company?.nom && `for ${company.nom}`}
        </h2>

        {/* --- Message centré dans le modal --- */}
        {alertOpen && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80%",
              maxWidth: 400,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: alertType === "error" ? "red" : "green",
              color: alertType === "error" ? "red" : "green",
              p: 2,
              borderRadius: 2,
              textAlign: "center",
              zIndex: 1000,
            }}
          >
            {alertMessage}
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <TextField
            label="Store Number"
            name="storeNumber"
            value={formData.storeNumber}
            disabled
            fullWidth
          />
          <TextField
            label="Company Name"
            name="companyName"
            value={formData.companyName}
            disabled
            fullWidth
          />
          <TextField
            label="Opening Date"
            name="openingDate"
            type="date"
            disabled
            value={
              formData.openingDate ? formData.openingDate.split("T")[0] : ""
            }
            InputLabelProps={{ shrink: true }}
          />
          {/* Move the isActive checkbox here */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isActive}
                onChange={handleCheckboxChange}
              />
            }
            label="Exploitant"
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            label="Phone Number"
            name="noCellulaire"
            value={formData.noCellulaire}
            onChange={handleChange}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Langue</InputLabel>
            <Select
              name="langue"
              value={formData.langue}
              onChange={handleChange}
            >
              <MenuItem value="FR">FR</MenuItem>
              <MenuItem value="EN">EN</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="% Action"
            name="pourcentageAction"
            type="number"
            value={formData.pourcentageAction}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Adresse"
            name="adresse"
            value={formData.adresse || ""}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Ville"
            name="ville"
            value={formData.ville || ""}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Code Postal"
            name="codePostal"
            value={formData.codePostal || ""}
            onChange={handleChange}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Pays</InputLabel>
            <Select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {countries.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Province</InputLabel>
            <Select
              value={province}
              onChange={(e) => setProvince(e.target.value)}
            >
              {provinces.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Region</InputLabel>
            <Select value={region} onChange={(e) => setRegion(e.target.value)}>
              {regions.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box mt={2} display="flex" justifyContent="flex-end" sx={{ gap: 2 }}>
          <Button variant="contained" onClick={handleSubmit}>
            Save
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </Box>

        {/* ✅ Ton Custom Alert */}
        <CustomAlert
          open={alertOpen}
          message={alertMessage}
          typeMessage={alertType}
          closeMessage={() => setAlertOpen(false)}
        />
      </Box>
    </Modal>
  );
}
